import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { addToast } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { getApiErrorMessage } from "@/utils/api-error";
import { describeErrorForLog } from "@/utils/sentry-error";
import { sentryLog } from "@/utils/sentry-utils";

type CustomMutationOptions<TData, TError, TVariables, TContext> = Omit<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  "onError"
> & {
  onError?: (
    error: TError,
    variables: TVariables,
    context: TContext | undefined,
    processedError: string | null,
    processedErrorCode: string | null,
  ) => void;
  displayNotification?: boolean; // Add the displayNotification option
};

type CustomMutationResult<TData, TError, TVariables, TContext> =
  UseMutationResult<TData, TError, TVariables, TContext> & {
    processedErrorMessage: string | null;
    processedErrorCode: string | null;
  };

export function useCustomMutation<
  TData,
  TError = AxiosError,
  TVariables = void,
  TContext = unknown,
>(
  options: CustomMutationOptions<TData, TError, TVariables, TContext>,
): CustomMutationResult<TData, TError, TVariables, TContext> {
  const { displayNotification = false, ...mutationOptions } = options; // Default to false for displayNotification

  const mutation = useMutation<TData, TError, TVariables, TContext>({
    ...mutationOptions,
    onError: (error, variables, context) => {
      if (error instanceof AxiosError && error?.status === 498) {
        addToast({
          title: t`Seja je potekla`,
          description: t`Seja je potekla, ponovno se prijavite`,
          color: "danger",
        });

        sentryLog.error(
          "Logout user - session timeout",
          describeErrorForLog(error),
        );

        localStorage.removeItem("usr_token");
        window.location.reload();
      }

      const processedErrorMessage = resolveErrorMessage(error);

      const processedErrorCode =
        error instanceof AxiosError && error?.response?.data?.errorCode;

      // Show the notification only if displayNotification is true
      if (displayNotification) {
        addToast({
          title: t`Prišlo je do napake`,
          description:
            processedErrorMessage || t`Prišlo je do nepričakovane napake`,
          color: "danger",
        });
      }

      // If the user provided an onError callback, call it with the processed error message
      if (mutationOptions.onError) {
        mutationOptions.onError(
          error,
          variables,
          context,
          processedErrorMessage,
          processedErrorCode,
        );
      }
    },
  });

  // Process the error message
  const processedErrorMessage = resolveErrorMessage(mutation.error);

  const processedErrorCode =
    mutation.error instanceof AxiosError
      ? mutation.error?.response?.data?.errorCode
      : null;

  return {
    ...mutation,
    processedErrorMessage,
    processedErrorCode,
  };
}

/**
 * Narrowed to AxiosError so a client-side thrown Error never leaks its
 * untranslated message into a toast.
 */
function resolveErrorMessage(error: unknown): string | null {
  if (!(error instanceof AxiosError)) {
    return null;
  }

  return getApiErrorMessage(error) ?? null;
}
